<template>
    <b-modal
        v-model="isShowing"
        title="Edit Associated Requirements"
        cancel-title="Cancel"
        ok-title="Save"
        size="xl"
        no-close-on-backdrop
        @cancel="$emit('close')"
        @ok="$emit('save')"
    >
        <div class="w-100 d-flex justify-content-between p-2">
            <div class="w-100 d-flex">
                <div class="w-25"><strong>State</strong></div>
                <div class="w-25"><strong>Discipline</strong></div>
                <div class="w-25"><strong>Requirement</strong></div>
            </div>
            <div class="d-flex" :style="{ minWidth: '124px' }"></div>
        </div>

        <template v-if="localAssocReq.length">
            <ValidationObserver
                v-for="(requirement, index) in localAssocReq"
                :key="`module-assoc-req-${index}-${requirement.id}`"
            >
                <b-form
                    slot-scope="{ validate }"
                    @submit.prevent="validate().then(() => handleSaveRow({ index, requirement }))"
                >
                    <div class="w-100 alias-container d-flex justify-content-between p-2 mb-2">
                        <div class="w-100 d-flex">
                            <ValidationProvider
                                v-if="isEditing[index]"
                                v-slot="{ errors }"
                                rules="required"
                                name="State"
                                slim
                            >
                                <div class="w-25 d-flex flex-column input-content">
                                    <v-select
                                        v-model="requirement.state_id"
                                        name="State 0"
                                        :options="states"
                                        placeholder="State"
                                        index="id"
                                        label="name"
                                    ></v-select>
                                    <span v-if="errors[0]" class="input-error-msg">{{ errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                            <div v-else class="w-25">
                                {{ statesList[requirement.state_id] }}
                            </div>

                            <ValidationProvider
                                v-if="isEditing[index]"
                                v-slot="{ errors }"
                                rules="required"
                                name="Discipline"
                                slim
                            >
                                <div class="w-25 d-flex flex-column input-content ml-4">
                                    <v-select
                                        v-model="requirement.discipline_id"
                                        name="State 0"
                                        :options="disciplineOptions"
                                        placeholder="Discipline"
                                        index="id"
                                        label="name"
                                    ></v-select>
                                    <span v-if="errors[0]" class="input-error-msg">{{ errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                            <div v-else class="w-25">
                                {{ disciplinesList[requirement.discipline_id] }}
                            </div>

                            <ValidationProvider
                                v-if="isEditing[index]"
                                v-slot="{ errors }"
                                rules="required"
                                name="Requirement(s)"
                                slim
                            >
                                <div class="w-25 d-flex flex-column input-content ml-4">
                                    <v-select
                                        v-model="requirement.assoc_req_id"
                                        name="State 0"
                                        :options="requirementOptions"
                                        placeholder="Requirement"
                                        index="id"
                                        label="name"
                                    ></v-select>
                                    <span v-if="errors[0]" class="input-error-msg">{{ errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                            <div v-else class="w-25">
                                {{ requirementsList[requirement.assoc_req_id] }}
                            </div>
                        </div>

                        <div class="d-flex mt-2">
                            <div>
                                <button
                                    v-if="!isEditing[index]"
                                    type="button"
                                    class="button green-content"
                                    @click="handleEdit({ index })"
                                >Edit</button>
                                <button
                                    v-else
                                    type="button"
                                    class="button gray-content"
                                    @click="cancelEditing({ index })"
                                >Cancel</button>
                            </div>
                            <div class="ml-2">
                                <button
                                    v-if="!isEditing[index]"
                                    type="button"
                                    class="button red-content"
                                    @click="handleRemoveAssocReq({ index, record: requirement })"
                                >Remove</button>
                                <button
                                    v-else
                                    type="submit"
                                    class="button green-content"
                                >Save</button>
                            </div>
                        </div>
                    </div>
                </b-form>
            </ValidationObserver>
        </template>

        <div v-else class="py-2 mb-2">
            No Associated Requirements yet.
        </div>

        <div class="">
            <button
                type="button"
                class="button green-content"
                @click="handleAddRequirement"
            >Add Requirement</button>
        </div>

        <div class="d-flex justify-content-end mt-4">
            <button type="button" class="button green-content ml-2" @click="$emit('close')">Done</button>
        </div>

        <template v-slot:modal-footer>
            <div></div>
        </template>
    </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import cloneDeep from 'lodash.clonedeep';

export default {
    components: {
        ValidationObserver,
        ValidationProvider,
	},

    props: {
        moduleId: {
            type: Number,
            required: true,
        },
        assocReq: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            isShowing: true,
            states: [],
            categories: [],
            localAssocReq: cloneDeep(this.assocReq),
            currentlyEditingIndexes: [],
        };
    },

    mounted() {
        this.getRegions();
        this.getCategories();
    },

    watch: {
        assocReq: {
            handler() {
                this.localAssocReq = cloneDeep(this.assocReq);
            },
            deep: true,
        },
    },

    computed: {
        statesList() {
            return this.states.reduce((acc, state) => {
                acc[state.id] = state.name;
                return acc;
            }, {});
        },

        disciplineOptions() {
            const DISCIPLINE_TYPE_ID = 3;
            return this.categories.filter(category => (category.sme_tag_type_id === DISCIPLINE_TYPE_ID));
        },

        disciplinesList() {
            return this.disciplineOptions.reduce((acc, discipline) => {
                acc[discipline.id] = discipline.name;
                return acc;
            }, {});
        },

        requirementOptions() {
            const REQUIREMENT_TYPE_ID = 5;
            return this.categories.filter(category => (category.sme_tag_type_id === REQUIREMENT_TYPE_ID));
        },

        requirementsList() {
            return this.requirementOptions.reduce((acc, requirement) => {
                acc[requirement.id] = requirement.name;
                return acc;
            }, {});
        },

        isEditing() {
            return this.localAssocReq.reduce((acc, item, index) => {
                if (this.currentlyEditingIndexes.includes(index)) {
                    acc[index] = true;
                } else {
                    acc[index] = false;
                }
                return acc;
            }, {});
        },
    },

    methods: {
        async getRegions() {
            const states = await this.$store.dispatch('getRegions', 'US');
            this.states = states;
        },

        async getCategories() {
            const params = {};
            const response = await this.$store.dispatch('getCategories', params);
            this.categories = response.data.data;
        },

        handleAddRequirement() {
            this.currentlyEditingIndexes.push(this.localAssocReq.length);
            this.localAssocReq.push({
                module_id: this.moduleId,
                state_id: null,
                discipline_id: null,
                assoc_req_id: null,
            });
        },

        handleEdit({ index }) {
            if (this.currentlyEditingIndexes.includes(index)) return;
            this.currentlyEditingIndexes.push(index);
        },

        cancelEditing({ index }) {
            if (!this.currentlyEditingIndexes.includes(index)) return;
            this.currentlyEditingIndexes = this.currentlyEditingIndexes.filter(item => item !== index);
            this.localAssocReq[index] = cloneDeep(this.assocReq[index]);
            this.$forceUpdate();
        },

        async handleRemoveAssocReq({ index, record }) {
            await this.$store.dispatch('removeAssocReqState', record);
            this.localAssocReq.splice(index, 1);
        },

        async handleSaveRow({ index, requirement }) {
            const formData = {
                ...requirement,
            };

            if (!formData.id) {
                await this.$store.dispatch('addAssocReqState', formData);
            } else {
                await this.$store.dispatch('updateAssocReqState', formData);
            }

            this.cancelEditing({ index });
            this.$emit('refresh');
            return;
        },
    },
};
</script>


// WEBPACK FOOTER //
// src/components/sme/SharedViews/EditAssocReqModal.vue